/**
 * How current the receivables are, for every screen that shows them
 * (Collection OS Phase 0; docs/product/phase-0/01-FRESHNESS.md).
 *
 * One read, `receivables_freshness()` (0037), which any member may make: the
 * source, timestamps and yes-or-no facts — never a device name, an error text
 * or a sheet id. The meaning is the domain's (`receivablesFreshness`), so
 * Today, Customers, an account, Reports and Settings cannot disagree. Re-read
 * every minute while the page is in view, when it comes back into view, and
 * when the browser is back online; the age is re-worked every half minute so
 * "3 minutes ago" stays true between reads.
 *
 * A failed read is said as such ("could not check"), never as "up to date",
 * and figures already shown keep their age.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { readFreshnessFacts, receivablesFreshness, type FreshnessFacts, type ReceivablesFreshness } from '@timely/domain';
import { supabase } from '../lib/supabaseClient';

const READ_EVERY_MS = 60_000;
const TICK_MS = 30_000;

export interface FreshnessReading {
    /** Null until the first answer arrives (or when none ever has). */
    freshness: ReceivablesFreshness | null;
    /** The last read failed: the answer shown, if any, is from the read before. */
    unavailable: boolean;
    /** The browser says it is offline: nothing new can arrive until it is back. */
    offline: boolean;
    now: Date;
}

/** `refreshKey`: a value that changes when the page knows the figures just changed (a sync it ran), to read again at once. */
export function useReceivablesFreshness(workspaceId: string, refreshKey?: unknown): FreshnessReading {
    const [facts, setFacts] = useState<FreshnessFacts | null>(null);
    const [unavailable, setUnavailable] = useState(false);
    const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && navigator.onLine === false);
    const [now, setNow] = useState(() => new Date());
    const issuedFor = useRef(workspaceId);

    const read = useCallback(async () => {
        issuedFor.current = workspaceId;
        const { data, error } = await supabase().rpc('receivables_freshness', { p_workspace: workspaceId });
        if (issuedFor.current !== workspaceId) return;
        setNow(new Date());
        if (error) { setUnavailable(true); return; }
        setUnavailable(false);
        setFacts(readFreshnessFacts(data));
    }, [workspaceId]);

    useEffect(() => {
        setFacts(null);
        setUnavailable(false);
        void read();
        const inView = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';
        const timer = setInterval(() => { if (inView()) void read(); }, READ_EVERY_MS);
        const tick = setInterval(() => setNow(new Date()), TICK_MS);
        const onVisible = () => { if (inView()) void read(); };
        const onOnline = () => { setOffline(false); void read(); };
        const onOffline = () => setOffline(true);
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
        return () => {
            clearInterval(timer);
            clearInterval(tick);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
        };
    }, [read]);

    const first = useRef(true);
    useEffect(() => {
        if (first.current) { first.current = false; return; }
        void read();
    }, [refreshKey, read]);

    return { freshness: facts ? receivablesFreshness(facts, now) : null, unavailable, offline, now };
}
